
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useInvitationManagement } from './useInvitationManagement';
import { useCompany } from '@/contexts/CompanyContext';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  role: string;
  last_activity?: string;
}

export interface CreateUserData {
  email: string;
  role: string;
}

export const useUserManagement = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, isCompanyAdmin, isSuperAdmin } = useAuth();
  const { companyProfile, loading: companyLoading } = useCompanyProfile();
  const { currentCompany } = useCompany();
  const { toast } = useToast();
  const { 
    invitations, 
    fetchInvitations, 
    createInvitation, 
    resendInvitation, 
    revokeInvitation 
  } = useInvitationManagement();

  const fetchUsers = useCallback(async () => {
    console.log('UserManagement: fetchUsers called with dependencies:', {
      hasUser: !!user,
      companyLoading,
      companyProfileId: companyProfile?.id
    });
    
    if (!user) {
      console.log('UserManagement: No user available');
      setUsers([]);
      setLoading(false);
      return [];
    }

    if (companyLoading) {
      console.log('UserManagement: Company profile still loading');
      return [];
    }

    if (!companyProfile?.id) {
      console.log('UserManagement: No company profile available');
      setUsers([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      
      console.log('UserManagement: Fetching users for company:', companyProfile.id);
      
      // Fetch users through company associations instead of profiles.company_id
      const { data: associations, error: associationsError } = await supabase
        .from('user_company_associations')
        .select(`
          user_id,
          role,
          is_active,
          profiles!inner(*)
        `)
        .eq('company_id', companyProfile.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (associationsError) {
        console.error('Error fetching user associations:', associationsError);
        throw associationsError;
      }

      console.log('UserManagement: Fetched user associations:', associations);
      
      // Extract profiles from associations
      const profiles = associations?.map(assoc => ({
        ...assoc.profiles,
        association_role: assoc.role
      })) || [];

      // Note: We no longer fetch from user_roles table for regular users
      // user_roles now only contains super_admin entries
      // Company-level roles (owner, admin, member) come from user_company_associations

      // Get last activity data for actual users only
      const userIds = profiles?.map(p => p.id) || [];
      const { data: activities, error: activitiesError } = await supabase
        .from('admin_logs')
        .select('admin_user_id, created_at')
        .in('admin_user_id', userIds)
        .order('created_at', { ascending: false });

      if (activitiesError) {
        console.warn('Could not fetch activities:', activitiesError);
      }

      // Combine data - only for actual system users
      const usersWithRoles = profiles?.map(profile => {
        const lastActivity = activities?.find(activity => activity.admin_user_id === profile.id);
        
        return {
          ...profile,
          // Use company-level role from user_company_associations
          role: profile.association_role || 'member',
          last_activity: lastActivity?.created_at
        };
      }) || [];

      console.log('UserManagement: Processed users with roles:', usersWithRoles);
      setUsers(usersWithRoles);
      
      // Also fetch invitations
      await fetchInvitations();
      
      return usersWithRoles;
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch users. Please check your permissions.",
        variant: "destructive"
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [user, companyProfile?.id, companyLoading, toast, fetchInvitations]);

  // Auto-fetch users when company changes
  useEffect(() => {
    if (currentCompany?.company_id && !companyLoading) {
      console.log('UserManagement: Company changed, clearing users and fetching new data');
      setUsers([]); // Clear current users optimistically
      fetchUsers();
    }
  }, [currentCompany?.company_id, companyLoading, fetchUsers]);

  // Use invitation system for user creation
  const inviteUser = useCallback(async (userData: CreateUserData) => {
    return await createInvitation({
      email: userData.email,
      role: userData.role
    });
  }, [createInvitation]);

  const updateUserRole = useCallback(async (userId: string, newRole: string) => {
    try {
      // Update the company-level role in user_company_associations
      // This is the new approach - we no longer use user_roles for company members
      const companyId = companyProfile?.id;
      
      if (!companyId) {
        toast({
          title: "Error",
          description: "No company context available",
          variant: "destructive"
        });
        return false;
      }

      const { error } = await supabase
        .from('user_company_associations')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('company_id', companyId);

      if (error) throw error;

      // Refresh the users list
      await fetchUsers();
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: "Error",
        description: "Failed to update user role",
        variant: "destructive"
      });
      return false;
    }
  }, [companyProfile?.id, toast, fetchUsers]);

  const removeUserFromCompany = useCallback(async (userId: string) => {
    try {
      const companyId = companyProfile?.id;
      
      if (!companyId) {
        toast({
          title: "Error",
          description: "No company context available",
          variant: "destructive"
        });
        return false;
      }

      const { error } = await supabase.rpc('remove_user_from_company', {
        p_user_id: userId,
        p_company_id: companyId,
        p_admin_user_id: user?.id
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User has been removed from the company",
      });

      // Refresh the users list
      await fetchUsers();
      return true;
    } catch (error) {
      console.error('Error removing user from company:', error);
      toast({
        title: "Error",
        description: "Failed to remove user from company",
        variant: "destructive"
      });
      return false;
    }
  }, [user, companyProfile?.id, toast, fetchUsers]);

  const logUserActivity = useCallback(async (activityType: string, details?: any) => {
    try {
      await supabase
        .from('admin_logs')
        .insert({
          admin_user_id: user?.id,
          action: activityType,
          target_type: 'user',
          target_id: user?.id?.toString(),
          details: details,
          user_agent: navigator.userAgent
        });
    } catch (error) {
      console.warn('Failed to log user activity:', error);
    }
  }, [user]);

  return {
    users,
    invitations,
    loading,
    fetchUsers,
    inviteUser, // This now uses the invitation system
    updateUserRole,
    removeUserFromCompany, // Company-specific removal (soft delete)
    logUserActivity,
    resendInvitation,
    revokeInvitation
  };
};
